import type { JSX } from 'react';

/**
 * Topbar — Phase 3 increment A skeleton.
 *
 * Holds:
 *  - Brand mark / Space switcher (left)
 *  - Language switcher (center) — bound to Space.locales in Phase 3 G
 *  - "Publish" button (right) — wired in Phase 3 C
 *  - User menu (far right) — wired to Sanctum logout in Phase 3 A
 *
 * For Phase 3 A we ship the visual structure with placeholder
 * handlers. Subsequent increments wire each control to real state.
 */
export interface TopbarLocaleOption {
  code: string;
  label: string;
}

export interface TopbarProps {
  spaceName: string | null;
  spaceLocaleCode: string;
  /** Optional — when supplied, renders a switcher. Empty list = badge-only. */
  availableLocales?: TopbarLocaleOption[];
  onLocaleChange?: (code: string) => void;
  /** True if there are unsaved changes — disables Publish, surfaces a warning. */
  hasUnsavedChanges?: boolean;
  /** Hooked up in increment C; for now a noop click handler is fine. */
  onPublishClick?: () => void;
  onLogoutClick?: () => void;
}

export function Topbar({
  spaceName,
  spaceLocaleCode,
  availableLocales,
  onLocaleChange,
  hasUnsavedChanges = false,
  onPublishClick,
  onLogoutClick,
}: TopbarProps): JSX.Element {
  const hasSwitcher =
    Array.isArray(availableLocales) && availableLocales.length > 1;
  return (
    <>
      <div className="flex items-center gap-3 font-semibold tracking-tight">
        <span aria-hidden="true" className="text-[#c9a44a]">
          ◆
        </span>
        <span>Peacock</span>
        {spaceName !== null ? (
          <span className="text-zinc-400">/ {spaceName}</span>
        ) : null}
      </div>

      <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
        {/* Tap-target floor 44px (Apple HIG) — responsive-audit-fixer
            2026-05-30. min-h-11 = 44px on Tailwind's default scale. */}
        {hasSwitcher && availableLocales !== undefined ? (
          <select
            value={spaceLocaleCode}
            onChange={(e) => onLocaleChange?.(e.target.value)}
            // max-w bumped to 260px + uppercase dropped: "DEUTSCH (ÖSTERREICH)"
            // is 17 chars and the uppercase rendering adds ~12% width, so
            // 200px was clipping the option AND the dropdown arrow
            // (ui-design-reviewer 2026-05-30 HIGH).
            className="min-h-11 min-w-0 max-w-[260px] rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-xs tracking-wider text-zinc-700 focus:border-zinc-900 focus:outline-none"
            aria-label="Aktive Sprache"
            data-testid="locale-switcher"
          >
            {availableLocales.map((loc) => (
              <option key={loc.code} value={loc.code}>
                {loc.code} — {loc.label}
              </option>
            ))}
          </select>
        ) : (
          <span
            className="inline-flex min-h-11 items-center rounded-md border border-zinc-200 px-3 py-2 font-mono text-xs uppercase tracking-wider text-zinc-600"
            title="Aktive Sprache"
          >
            {spaceLocaleCode}
          </span>
        )}

        <button
          type="button"
          disabled={!hasUnsavedChanges}
          onClick={onPublishClick}
          title={
            hasUnsavedChanges
              ? 'Ungespeicherte Änderungen veröffentlichen'
              : 'Keine ungespeicherten Änderungen — bearbeite zuerst ein Feld'
          }
          aria-label={
            hasUnsavedChanges
              ? 'Ungespeicherte Änderungen veröffentlichen'
              : 'Veröffentlichen (deaktiviert — keine ungespeicherten Änderungen)'
          }
          // Disabled state had bg-zinc-300 + text-white = ~1.6:1 contrast
          // (WCAG AA needs 4.5:1 for normal text). Swap to bg-zinc-100
          // + text-zinc-400 to bring it above the floor while keeping
          // the disabled affordance obvious (ui-design-reviewer
          // 2026-05-30 HIGH).
          className="min-h-11 rounded-md bg-zinc-900 px-3 py-2 text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400"
        >
          Veröffentlichen
        </button>

        <button
          type="button"
          onClick={onLogoutClick}
          className="min-h-11 rounded-md border border-zinc-200 px-3 py-2 text-zinc-700 hover:bg-zinc-100"
        >
          Abmelden
        </button>
      </div>
    </>
  );
}
